/* eslint-disable no-prototype-builtins */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Container, Link } from '@olxbr/ds-web'

import Form from '../../atoms/Form'
import { View } from '../../atoms/View'

import { OauthLoginCallout } from '../../molecules/OauthLoginCallout'
import HelpFooter from '../../molecules/HelpFooter'
import TermsAndPrivacyNotice from '../../molecules/TermsAndPrivacyNotice'

import { HelpModalTemplate } from '../HelpModalTemplate'

import MailCheckInput from '../../organisms/MailCheckInput'
import IdentifierInput from '../../organisms/IdentifierInput'
import LoginActionButton from '../../atoms/LoginActionButton'
import { isLoginScrollEnabled, isCheckIdentifierExperimentOn } from '../../../util/experiments'

const TWO_COL_BP = '1024px'

const getContentElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const FormContent = styled(View)`
  display: flex;
  flex-direction: column;

  ${props =>
    !props.modalMode &&
    `
      padding-top: var(--spacing-3);
      padding-left: var(--spacing-7);
      padding-right: var(--spacing-7);
  `}
`

const PageForm = styled(Form)`
  padding-bottom: ${props => (props.modalMode ? `var(--spacing-2)` : `var(--spacing-5)`)};
`

const Content = styled(getContentElement)`
  && {
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    margin: auto;
    max-width: 466px;
    padding: 0;
    ${props => !props.modalMode && `padding-top: var(--spacing-3)`};
  }
`

const FooterWrapper = styled(View)`
  display: flex;
  justify-content: center;
  max-width: 466px;
  margin: auto;
  margin-top: var(--spacing-1);
  text-align: center;
`

const TextCenter = styled.div`
  text-align: center;
`

const PageWrapper = styled(View)`
  ${props => !props.modalMode && 'margin-top: var(--spacing-6)'};
`

const NewPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 88px);
  overflow: hidden;
  @media (min-width: ${TWO_COL_BP}) {
    margin-top: var(--spacing-6);
    height: auto;
    overflow: visible;
    display: block;
  }
`

const TwoColumnWrapper = styled.div`
  margin: 0 auto;
  flex: 1;
  @media (min-width: ${TWO_COL_BP}) {
    display: flex;
    align-items: stretch;
    max-width: 1040px;
    margin: 0 auto;
    padding-top: var(--spacing-6);
    gap: var(--spacing-7);
    flex: none;
  }
`

const LeftColumn = styled.div`
  display: none;
  @media (min-width: ${TWO_COL_BP}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
  }
`

const RightColumn = styled.div`
  @media (min-width: ${TWO_COL_BP}) {
    width: 504px;
    flex-shrink: 0;
  }
`

const NewContent = styled(getContentElement)`
  && {
    flex: 1;
    flex-direction: column;
    padding: 0;
    border: none;
    box-shadow: none;
    @media (min-width: ${TWO_COL_BP}) {
      border: var(--border-width-0-1) solid var(--container-border-color-outlined);
      border-radius: var(--border-radius-1);
      padding: var(--spacing-3) var(--spacing-4);
    }
  }
`

const NewFormContent = styled(View)`
  display: flex;
  flex-direction: column;
  padding: var(--spacing-1) var(--spacing-2) 0;
  @media (min-width: ${TWO_COL_BP}) {
    padding: var(--spacing-2) 0 0;
  }
`

const MobileHeader = styled.div`
  text-align: center;
  padding-top: 24px;
  @media (min-width: ${TWO_COL_BP}) {
    display: none;
  }
`

const DesktopFooter = styled.div`
  margin-top: auto;
  padding-top: var(--spacing-7);
`

const LogoAlignLeft = styled.div`
  & > * {
    margin-left: 0 !important;
    margin-right: auto !important;
  }
`

const MobileFooter = styled.div`
  display: flex;
  justify-content: center;
  max-width: 466px;
  margin: auto;
  margin-top: auto;
  text-align: center;
  padding: var(--spacing-1) var(--spacing-2);
  @media (min-width: ${TWO_COL_BP}) {
    display: none;
  }
`

const EmailLoginTemplate = ({
  abTests,
  facebookAppId,
  onMagicLinkClick,
  onRequireEmail,
  onSubmit,
  onRegisterClick,
  onHelpLinkClick,
  onForgotPassword,
  onReactivationLinkClick,
  onHelpLinkClose,
  onMailCheckSuggestionClick,
  onOauthCancel,
  onOauthError,
  onOauthSubmit,
  onOauthSuccess,
  onPrivacyLinkClick,
  onTermsLinkClick,
  isSubmitting,
  showHelpingModalLink = true,
  shouldDisableSubmit,
  inputWrapper,
  errorMsg,
  captchaChallenge,
  lurker,
  tracker,
  onPhoneNotValidatedError,
  onChallengePinCodeCall,
  sessionTokens,
  onAccountNotValidated,
  onAccountMustValidate,
  modalMode,
  onClickNeedHelp,
  onAccountSocialLoginTyping,
  onAccountCompleteData,
  theme,
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false)
  const checkIdentifier = isCheckIdentifierExperimentOn(abTests)

  if (isLoginScrollEnabled(abTests) && !modalMode) {
    return (
      <NewPageWrapper>
        <TwoColumnWrapper>
          <LeftColumn>
            {theme.LOGO ? (
              <LogoAlignLeft>
                <theme.LOGO />
              </LogoAlignLeft>
            ) : null}
            <span className="typo-title-large mb-1">
              Entre e negocie com <strong> segurança e rapidez!</strong>
            </span>
            <span className="typo-body-large mb-4">
              Acesse e aproveite para apegar e desapegar na {theme.BRAND.NAME}
            </span>
            <DesktopFooter>
              <TermsAndPrivacyNotice
                onPrivacyClick={onPrivacyLinkClick}
                onTermsClick={onTermsLinkClick}
              />
            </DesktopFooter>
          </LeftColumn>
          <RightColumn>
            <NewContent type={theme.CONTAINER.TYPE} modalMode={false}>
              <MobileHeader>
                <NewFormContent>
                  <span className="typo-body-medium mb-1">
                    Entre e negocie com <strong>segurança e rapidez!</strong>
                  </span>
                </NewFormContent>
              </MobileHeader>
              <NewFormContent>
                <OauthLoginCallout
                  facebookAppId={facebookAppId}
                  onCancel={onOauthCancel}
                  onError={onOauthError}
                  onRequireEmail={onRequireEmail}
                  onSubmit={onOauthSubmit}
                  onUserLoggedIn={onOauthSuccess}
                  lurker={lurker}
                  tracker={tracker}
                  onMagicLinkClick={onMagicLinkClick}
                  abTests={abTests}
                  onPhoneNotValidatedError={onPhoneNotValidatedError}
                  onChallengePinCodeCall={onChallengePinCodeCall}
                  sessionTokens={sessionTokens}
                  onAccountNotValidated={onAccountNotValidated}
                  onAccountMustValidate={onAccountMustValidate}
                  modalMode={modalMode}
                  onAccountSocialLoginTyping={onAccountSocialLoginTyping}
                  onAccountCompleteData={onAccountCompleteData}
                />
              </NewFormContent>
              <NewFormContent>
                {errorMsg}
                <Form method="POST" onSubmit={onSubmit} noValidate>
                  {inputWrapper(({ field, fieldState: { error, ...restFields } }) => {
                    const inputProps = {
                      disabled: isSubmitting,
                      onSuggestionClick: onMailCheckSuggestionClick,
                      ...field,
                      ...restFields,
                      hasError: !!error?.message,
                      error: error?.message,
                    }

                    return checkIdentifier ? (
                      <IdentifierInput {...inputProps} />
                    ) : (
                      <MailCheckInput label="Insira seu e-mail" {...inputProps} />
                    )
                  })}
                  {captchaChallenge || null}
                  <div className="flex mt-2 flex-col gap-2">
                    {theme.BUTTONS.map(({ TEXT, VARIANT, COLOR, ICON, ACTION, HREF, TRACKING }) => (
                      <LoginActionButton
                        key={TEXT}
                        action={ACTION}
                        color={COLOR}
                        href={HREF}
                        icon={ICON}
                        isSubmitting={isSubmitting}
                        lurker={lurker}
                        onSubmit={onSubmit}
                        shouldDisableSubmit={shouldDisableSubmit}
                        text={ACTION === 'login' ? 'Continuar' : TEXT}
                        tracking={TRACKING}
                        variant={VARIANT}
                      />
                    ))}
                  </div>
                </Form>
              </NewFormContent>
              <div className="flex justify-center pt-4 pb-2">
                <span className="typo-body-small">Não tem uma conta?</span>
                <Link size="small" onClick={onRegisterClick} marginLeft="--spacing-0-5">
                  Cadastre-se
                </Link>
              </div>
            </NewContent>
          </RightColumn>
        </TwoColumnWrapper>
        <MobileFooter>
          <TermsAndPrivacyNotice
            onPrivacyClick={onPrivacyLinkClick}
            onTermsClick={onTermsLinkClick}
          />
        </MobileFooter>
        <HelpModalTemplate
          abTests={abTests}
          open={showHelpModal}
          onForgotPasswordLinkClick={onForgotPassword}
          onReactivationLinkClick={onReactivationLinkClick}
          onMagicLinkClick={onMagicLinkClick}
          onClose={() => {
            setShowHelpModal(false)
            onHelpLinkClose()
          }}
        />
      </NewPageWrapper>
    )
  }

  return (
    <PageWrapper modalMode={modalMode}>
      <Content type={theme.CONTAINER.TYPE} modalMode={modalMode}>
        <TextCenter>
          <FormContent modalMode={modalMode}>
            {theme.LOGO ? <theme.LOGO /> : null}
            <span className="typo-title-medium mb-1">
              Entre na sua conta e <br /> negocie com segurança!
            </span>
            <span className="typo-body-medium mb-4">
              Acesse e aproveite uma experiência <br /> segura dentro d{theme.BRAND.ARTICLE}{' '}
              {theme.BRAND.NAME}.
            </span>

            <OauthLoginCallout
              facebookAppId={facebookAppId}
              onCancel={onOauthCancel}
              onError={onOauthError}
              onRequireEmail={onRequireEmail}
              onSubmit={onOauthSubmit}
              onUserLoggedIn={onOauthSuccess}
              lurker={lurker}
              tracker={tracker}
              onMagicLinkClick={onMagicLinkClick}
              abTests={abTests}
              onPhoneNotValidatedError={onPhoneNotValidatedError}
              onChallengePinCodeCall={onChallengePinCodeCall}
              sessionTokens={sessionTokens}
              onAccountNotValidated={onAccountNotValidated}
              onAccountMustValidate={onAccountMustValidate}
              modalMode={modalMode}
              onAccountSocialLoginTyping={onAccountSocialLoginTyping}
              onAccountCompleteData={onAccountCompleteData}
            />
          </FormContent>
        </TextCenter>
        {!errorMsg && (
          <div
            className="flex"
            style={{ marginBottom: modalMode ? 'var(--spacing-4)' : 'var(--spacing-0-5)' }}
          />
        )}
        <FormContent modalMode={modalMode}>
          {errorMsg}
          <PageForm method="POST" onSubmit={onSubmit} noValidate modalMode={modalMode}>
            {inputWrapper(({ field, fieldState: { error, ...restFields } }) => (
              <MailCheckInput
                label="E-mail"
                disabled={isSubmitting}
                onSuggestionClick={onMailCheckSuggestionClick}
                {...field}
                {...restFields}
                hasError={!!error?.message}
              />
            ))}
            {captchaChallenge || null}
            <div className="flex mt-4 flex-col gap-2">
              {theme.BUTTONS.map(({ TEXT, VARIANT, COLOR, ICON, ACTION, HREF, TRACKING }) => (
                <LoginActionButton
                  key={TEXT}
                  action={ACTION}
                  color={COLOR}
                  href={HREF}
                  icon={ICON}
                  isSubmitting={isSubmitting}
                  lurker={lurker}
                  onSubmit={onSubmit}
                  shouldDisableSubmit={shouldDisableSubmit}
                  text={TEXT}
                  tracking={TRACKING}
                  variant={VARIANT}
                />
              ))}
            </div>
          </PageForm>
        </FormContent>
        <div
          className="flex justify-center"
          style={{
            paddingTop: modalMode ? 'var(--spacing-2)' : 'var(--spacing-4)',
            paddingBottom: modalMode ? 'var(--spacing-2)' : 'var(--spacing-4)',
          }}
        >
          <span className="typo-body-small">Não tem uma conta?</span>
          <Link size="small" onClick={onRegisterClick} marginLeft="--spacing-0-5">
            Cadastre-se
          </Link>
        </div>
        {showHelpingModalLink && (
          <HelpFooter
            onClick={() => {
              if (modalMode) {
                onClickNeedHelp && onClickNeedHelp()
              } else {
                setShowHelpModal(true)
                onHelpLinkClick()
              }
            }}
          />
        )}
      </Content>
      <FooterWrapper>
        <TermsAndPrivacyNotice
          onPrivacyClick={onPrivacyLinkClick}
          onTermsClick={onTermsLinkClick}
        />
      </FooterWrapper>
      <HelpModalTemplate
        abTests={abTests}
        open={showHelpModal}
        onForgotPasswordLinkClick={onForgotPassword}
        onReactivationLinkClick={onReactivationLinkClick}
        onMagicLinkClick={onMagicLinkClick}
        onClose={() => {
          setShowHelpModal(false)
          onHelpLinkClose()
        }}
      />
    </PageWrapper>
  )
}

EmailLoginTemplate.propTypes = {
  abTests: PropTypes.string,
  facebookAppId: PropTypes.string,
  onMagicLinkClick: PropTypes.func,
  onRegisterClick: PropTypes.func,
  onRequireEmail: PropTypes.func,
  onHelpLinkClick: PropTypes.func,
  onOauthCancel: PropTypes.func,
  onOauthError: PropTypes.func,
  onOauthSubmit: PropTypes.func,
  onOauthSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
  onPrivacyLinkClick: PropTypes.func,
  onTermsLinkClick: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onReactivationLinkClick: PropTypes.func,
  onHelpLinkClose: PropTypes.func,
  onMailCheckSuggestionClick: PropTypes.func,
  showReactivationLink: PropTypes.bool,
  smartLockLogin: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  showHelpingModalLink: PropTypes.bool,
  shouldDisableSubmit: PropTypes.bool,
  errorMsg: PropTypes.element,
  captchaChallenge: PropTypes.element,
  lurker: PropTypes.object,
  tracker: PropTypes.object,
  onPhoneNotValidatedError: PropTypes.func,
  onChallengePinCodeCall: PropTypes.func,
  sessionTokens: PropTypes.object,
  onAccountNotValidated: PropTypes.func,
  onAccountMustValidate: PropTypes.func,
  modalMode: PropTypes.bool,
  onClickNeedHelp: PropTypes.func,
  onAccountSocialLoginTyping: PropTypes.func,
  onAccountCompleteData: PropTypes.func,
  theme: PropTypes.object,
}

export { EmailLoginTemplate }
